/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState } from 'react';

import { I18n } from '@adp-arch/i18n';
import { Input, Button, Typography } from '@coze-arch/coze-design';
import { IconCozEdit, IconCozWarningCircleFillPalette } from '@coze-arch/coze-design/icons';
import { IconButton } from '@coze-arch/coze-design';
import classNames from 'classnames';

import s from './index.module.less';

interface ChangePasswordFieldProps {
  onSave: (oldPassword: string, newPassword: string) => Promise<void>;
  className?: string;
}

/**
 * Password complexity validation
 * At least 8 characters with mixed-case alphabetic characters, numerals and special characters
 */
const validatePasswordComplexity = (password: string): string | null => {
  if (password.length < 8) {
    return I18n.t('password_policy_min_length') || 'Password must be at least 8 characters';
  }
  if (!/[A-Z]/.test(password)) {
    return I18n.t('password_policy_uppercase') || 'Password must contain at least one uppercase letter';
  }
  if (!/[a-z]/.test(password)) {
    return I18n.t('password_policy_lowercase') || 'Password must contain at least one lowercase letter';
  }
  if (!/[0-9]/.test(password)) {
    return I18n.t('password_policy_digit') || 'Password must contain at least one digit';
  }
  if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/~`]/.test(password)) {
    return I18n.t('password_policy_special') || 'Password must contain at least one special character';
  }
  return null;
};

export const ChangePasswordField: React.FC<ChangePasswordFieldProps> = ({
  onSave,
  className,
}) => {
  const [isEdit, setEdit] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    // Validate new password complexity
    const complexityError = validatePasswordComplexity(newPassword);
    if (complexityError) {
      setError(complexityError);
      return;
    }

    // Validate confirm password matches
    if (newPassword !== confirmPassword) {
      setError(I18n.t('password_confirm_mismatch') || 'Passwords do not match');
      return;
    }

    // Validate old password is not empty
    if (!oldPassword) {
      setError(I18n.t('password_old_required') || 'Old password is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await onSave(oldPassword, newPassword);
      setEdit(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setError((err as Error).message || I18n.t('password_change_failed') || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEdit(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  if (!isEdit) {
    return (
      <div className={classNames(s['filed-readonly'], className)}>
        <Typography.Text
          fontSize="14px"
          className="!font-medium coz-fg-primary"
          ellipsis
        >
          {'******'}
        </Typography.Text>
        <IconButton
          icon={<IconCozEdit />}
          size="mini"
          color="secondary"
          className="ml-[8px]"
          onClick={() => setEdit(true)}
        />
      </div>
    );
  }

  return (
    <div className={classNames(s['field-edit'], className, 'flex-col !items-start gap-[8px]')}>
      <div className="w-full">
        <Typography.Text fontSize="12px" className="coz-fg-secondary mb-[4px] block">
          {I18n.t('password_old_label') || 'Current Password'}
        </Typography.Text>
        <Input
          mode="password"
          value={oldPassword}
          onChange={setOldPassword}
          autoFocus
          placeholder={I18n.t('password_old_placeholder') || 'Enter current password'}
        />
      </div>
      <div className="w-full">
        <Typography.Text fontSize="12px" className="coz-fg-secondary mb-[4px] block">
          {I18n.t('password_new_label') || 'New Password'}
        </Typography.Text>
        <Input
          mode="password"
          value={newPassword}
          onChange={setNewPassword}
          placeholder={I18n.t('password_new_placeholder') || 'Enter new password'}
        />
        <Typography.Text fontSize="10px" className="coz-fg-tertiary mt-[2px] block">
          {I18n.t('password_policy_hint') || 'At least 8 characters with uppercase, lowercase, numbers and special characters'}
        </Typography.Text>
      </div>
      <div className="w-full">
        <Typography.Text fontSize="12px" className="coz-fg-secondary mb-[4px] block">
          {I18n.t('password_confirm_label') || 'Confirm New Password'}
        </Typography.Text>
        <Input
          mode="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder={I18n.t('password_confirm_placeholder') || 'Confirm new password'}
          onEnterPress={handleSave}
        />
      </div>
      {error && (
        <div className="flex items-center gap-[4px] text-[12px] coz-fg-hglt-red">
          <IconCozWarningCircleFillPalette />
          {error}
        </div>
      )}
      <div className="flex gap-[8px] mt-[4px]">
        <Button
          className={s.btn}
          color="primary"
          loading={loading}
          onClick={handleCancel}
        >
          {I18n.t('Cancel')}
        </Button>
        <Button
          disabled={!oldPassword || !newPassword || !confirmPassword}
          className={s.btn}
          loading={loading}
          onClick={handleSave}
        >
          {I18n.t('setting_name_save')}
        </Button>
      </div>
    </div>
  );
};
